import { useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useComponentsField } from '@atlassian/jira-issue-field-components-field/src/services/use-components-field/index.tsx';
import type { ComponentsFieldValue } from '@atlassian/jira-issue-field-components-field/src/services/use-components-field/types.tsx';
import { COMPONENTS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component/index.tsx';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler/index.tsx';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
} from '../../../clipboard-handler/utils.tsx';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
} from '../../../types.tsx';
import { componentsSupportedTypes } from '../supported-field-types/index.tsx';
import type { TransformedComponentsField, TransformedComponentsFieldOption } from '../types.tsx';

const isTransformedComponentsOption = (
	option: unknown,
): option is TransformedComponentsFieldOption =>
	!!option && typeof option === 'object' && 'componentId' in option && 'name' in option;

const isTransformedComponents = (
	fieldValue: unknown,
): fieldValue is TransformedComponentsFieldOption[] =>
	Array.isArray(fieldValue) && fieldValue.every(isTransformedComponentsOption);

const isValidComponentsValue = (fieldValue: unknown): fieldValue is ComponentsFieldValue =>
	Array.isArray(fieldValue) &&
	fieldValue.every(
		(option: unknown) =>
			!!option && typeof option === 'object' && 'id' in option && 'name' in option,
	);

const emptyValue: [] = [];

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): ComponentsFieldValue => {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, componentsSupportedTypes)
	) {
		return [];
	}

	if (!isValidComponentsValue(metadata?.fieldValue)) {
		return [];
	}

	return metadata?.fieldValue;
};

function assertComponentsField(
	field: ListIssueFieldTransformed,
): asserts field is TransformedComponentsField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (field.type !== COMPONENTS_TYPE) {
		throw new Error('Field type is not supported');
	}
}

const useComponentsClipboardHandler: PasteHandler = (
	issue: { key: string; issueId: string },
	field: ListIssueFieldTransformed,
) => {
	assertComponentsField(field);

	const [{ value }, { saveValue }] = useComponentsField({
		issueKey: issue.key,
		initialValue: isTransformedComponents(field?.value)
			? field.value.map((component) => ({
					id: component.componentId,
					name: component.name,
				}))
			: emptyValue,
	});

	const saveValueFn = useCallback(
		(valueToSave: ComponentsFieldValue | null, meta: null, analyticsEvent: UIAnalyticsEvent) =>
			/**
			 * Wrap saveValue to make sure the saved value is never set to `null`
			 */
			saveValue(valueToSave ?? [], meta, analyticsEvent),
		[saveValue],
	);

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value?.map(({ name }) => name).join(', ') || '',
		currentValue: value,
		getPastedValue,
		saveValue: saveValueFn,
	});
};

const ComponentsClipboardHelper = createClipboardHelperComponent(useComponentsClipboardHandler);

export const componentsClipboardHandler = {
	[COMPONENTS_TYPE]: ComponentsClipboardHelper,
};
